/**
 * App — Server-backed deal persistence.
 * Deals stored in MongoDB via /api/deals. Settings via /api/settings.
 * localStorage is a working buffer + open-file session only.
 * Once a deal has been saved (currentFileId set), every data point
 * (inputs, assumptions, scenarios, notes, cadastre, market, FX, cash)
 * debounce-PUTs to Mongo. On boot/open, the server deal is the source of
 * truth across devices. Drafts still require explicit Save As.
 */
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import '@/App.css';
import { useConfig } from './context/ConfigContext';
import { DealCurrencyProvider } from './context/DealCurrencyContext';
import { KPIHeader, MobileAnalysisTabBar, nextProjectView } from './components/KPIHeader';
import { InputPanel } from './components/InputPanel';
import { OutputPanel } from './components/OutputPanel';
import { AssumptionsModal } from './components/AssumptionsModal';
import {
  SaveAsDialog, deriveNameFromAddress,
  createDealDetailed, updateDeal, updateDealDetailed, deleteDeal, archiveDeal,
  fetchSettings, saveSettings, migrateFromLocalStorage, fetchDeal, checkDealAccess,
} from './components/SnapshotManager';
import { GoogleAuthGate, UserMenu } from './components/GoogleAuthGate';
import { DecisionCockpit } from './components/DecisionCockpit';
import { ProjectSidebar } from './components/ProjectSidebar';
import { ErrorBoundary } from './components/ErrorBoundary';
import { DocumentPreviewPage } from './components/DocumentPreviewPage';
import { SharedAnalysisPage } from './components/SharedAnalysisPage';
import { ShareDialog } from './components/ShareDialog';
import { BuildFreshnessBanner } from './components/BuildFreshnessBanner';
import { API_BASE_URL } from './config/api';
import { authLoginUrl, persistErrorMessage, SESSION_EXPIRED_SAVE_MSG } from './utils/apiAuth';
import { validateInputs, inputsNeededClamping } from './types/dealTypes';
import { buildMarketQueryKey } from './utils/marketData';
import { applyCountryLocals } from './utils/countryPresets';
import {
  getOpenFileSession,
  withOpenFileSession,
  AUTOSAVE_KEY,
  bumpSessionEpoch,
  isSessionEpochCurrent,
} from './utils/openFileSession';
import {
  SERVER_AUTOSAVE_MS,
  buildDealPersistPayload,
  createSerialDealSaver,
  dealPersistFingerprint,
  shouldServerAutosave,
} from './utils/serverAutosave';
import {
  deepClone,
  hydrateScenarios,
  getActiveScenario,
  findScenario,
  preferredScenario,
  activateScenario,
  syncActiveScenario,
  duplicateScenario,
  renameScenario,
  deleteScenario,
  uniqueCopyName,
  resolveVersionAssumptionsOnOpen,
} from './utils/dealScenarios';
import { ScenarioVersionControls } from './components/ScenarioVersionControls';
import { ApproveBaselineDialog } from './components/ApproveBaselineDialog';
import { CashTrackerView } from './components/CashTrackerView';
import { LandAssessmentView } from './components/LandAssessmentView';
import { StageFinanceView } from './components/StageFinanceView';
import { FinancialDriversPanel } from './components/FinancialDriversPanel';
import {
  buildApprovedBaseline,
  createCashTracker,
  hydrateCashTracker,
  isLaunched,
} from './utils/cashTracker';
import {
  getAssessmentForScenario,
  hydrateLandAssessmentStore,
  buildCombinedVerdict,
} from './utils/landAssessment';
import {
  getStageFinanceAssumptionsForScenario,
  hydrateStageFinanceAssumptionsStore,
  upsertStageFinanceAssumptionsForScenario,
} from './utils/stageFinanceAssumptions';
import { materializeOpenedDeal, resolveBootDealFromServer } from './utils/dealServerHydrate';
import { applyEmptyDraft, identityChfFx, materializeEmptyDraft } from './utils/emptyDraft';
import { dealVerdictPatchFromServer } from './utils/sidebarVerdict';

const API = API_BASE_URL;
const DEBOUNCE_MS = 150;

async function fetchCalculation(inputs, assumptions) {
  try {
    const { address, comment, locationData, parkingSpotsManual, ...calcInputs } = inputs;
    const res = await fetch(`${API}/api/calculate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ inputs: calcInputs, assumptions }),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch { return null; }
}

function fxToSnapshot(fx) {
  if (!fx || fx.rate == null) return null;
  return {
    base: fx.base,
    quote: fx.quote,
    rate: fx.rate,
    asOf: fx.asOf,
    source: fx.source,
  };
}

function loadAutosave() {
  try {
    const raw = localStorage.getItem(AUTOSAVE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.inputs) return parsed;
    // QA-008: clamp extreme/invalid drafts before restoring
    const cleaned = validateInputs(parsed.inputs);
    if (inputsNeededClamping(parsed.inputs)) {
      return { ...parsed, inputs: cleaned, draftWasClamped: true };
    }
    return { ...parsed, inputs: cleaned };
  } catch { /* ignore */ }
  return null;
}

// Empty outputs — shown before first backend response
const EMPTY_OUTPUTS = {
  grossBuildableArea: 0, netSaleableArea: 0, numberOfUnits: 0, unitsAutoCalculated: false,
  avgSqmPerUnit: 0, pricePerUnitWithoutParking: 0, pricePerUnitWithParking: 0,
  pricePerUnitMin: 0, pricePerUnitMax: 0, parkingsPerUnit: 0, parkingValuePerUnit: 0,
  pricePositioning: 'market', priceDelta: 0,
  salesRevenue: 0, parkingRevenue: 0, totalRevenue: 0,
  landCost: 0, notaryFees: 0, notaryFeesRate: 0, otherLandFees: 0, otherLandFeesRate: 0, totalLandCost: 0,
  constructionCost: 0, constructionRate: 0, constructionReserve: 0, constructionReserveRate: 0, totalConstructionCost: 0,
  architectureCost: 0, architectureRate: 0, consultantsCost: 0, consultantsRate: 0,
  contingencyCost: 0, contingencyRate: 0, softCosts: 0,
  commercializationCost: 0, commercializationRate: 0, pilotageCost: 0, pilotageRate: 0,
  totalDevCost: 0, profitBeforeTax: 0, taxAmount: 0, taxRate: 0, profitAfterTax: 0,
  netAfterFinancing: 0, profitShareAmount: 0, finalNetProfit: 0,
  equityRequired: 0, equityRate: 0, equityBase: 0, equityBaseSource: '',
  bankDebtAmount: 0, bankDebtRate: 0, bankDebtBase: 0, bankDebtBaseSource: '', constructionFacilityAmount: 0,
  privateCapitalAmount: 0, privateCapitalRate: 0, privateCapitalBase: 0, privateCapitalBaseSource: '', privateCapitalType: 'debt',
  fundingTotal: 0, fundingCoverageRatio: 0,
  upfrontFees: 0, feesRate: 0, feesBase: 0, feesBaseSource: '',
  feasibilityStudy: 0, capitalInvested: 0,
  bankInterestCost: 0, bankSetupCost: 0, privateDebtCost: 0, privateDebtFees: 0,
  equityStructuringCost: 0, totalFinancingCost: 0,
  financingLabel: 'Equity only', bankDurationMonths: 18, bankInterestRate: 0.02, pcProfitShare: 0,
  totalCost: 0, roiOnEquity: 0, marginPercent: 0, landToRevenueRatio: 0,
  pricePerSqmEffective: 0, costPerSqm: 0,
};
const EMPTY_DECISION = { verdict: '...', reason: 'Calculating...', warnings: [] };
/** Mobile header = 34px KPI + 40px version bar; desktop KPI strip = 30px. */

function AnalysisPage({ user }) {
  const location = useLocation();
  const { assumptions: backendDefaults, sampleInputs, countryPresets, sourceLabels } = useConfig();

  const freshInputs = useCallback(() => {
    return { ...sampleInputs, address: '', comment: '', overrides: {}, capitalSources: { ...sampleInputs.capitalSources } };
  }, [sampleInputs]);

  /** New analysis always starts on Swiss defaults — not last deal or saved user settings. */
  const freshAssumptions = useCallback(() => {
    return applyCountryLocals({ ...backendDefaults }, 'CH', countryPresets);
  }, [backendDefaults, countryPresets]);

  const autosave = loadAutosave();
  const openFileSession = getOpenFileSession(autosave);
  const bootRef = useRef(null);
  if (bootRef.current == null) {
    const seedInputs = autosave?.inputs || { ...sampleInputs, address: '', comment: '', overrides: {}, capitalSources: { ...sampleInputs.capitalSources } };
    const seedAssumptions = autosave?.assumptions || { ...backendDefaults };
    bootRef.current = hydrateScenarios({
      inputs: seedInputs,
      assumptions: seedAssumptions,
      scenarios: autosave?.scenarios,
      activeScenarioId: autosave?.activeScenarioId,
    });
  }
  const boot = bootRef.current;
  const bootActive = getActiveScenario(boot.scenarios, boot.activeScenarioId);
  const [inputs, setInputsRaw] = useState(() => validateInputs(bootActive?.inputs || { ...sampleInputs, address: '', comment: '', overrides: {}, capitalSources: { ...sampleInputs.capitalSources } }));
  const setInputs = useCallback((next) => {
    setInputsRaw((prev) => {
      const resolved = typeof next === 'function' ? next(prev) : next;
      return validateInputs(resolved);
    });
  }, []);
  const [assumptions, setAssumptionsRaw] = useState(() => bootActive?.assumptions || { ...backendDefaults });
  const [scenarios, setScenarios] = useState(() => boot.scenarios);
  const [activeScenarioId, setActiveScenarioId] = useState(() => boot.activeScenarioId);
  const [cashTracker, setCashTracker] = useState(() => hydrateCashTracker(autosave?.cashTracker));
  const [landAssessment, setLandAssessment] = useState(() => hydrateLandAssessmentStore(autosave?.landAssessment));
  const [stageFinanceAssumptions, setStageFinanceAssumptions] = useState(() => hydrateStageFinanceAssumptionsStore(autosave?.stageFinanceAssumptions));
  const [workspace, setWorkspace] = useState('evaluation');
  const [launchOpen, setLaunchOpen] = useState(false);
  const [launchBusy, setLaunchBusy] = useState(false);
  const [launchError, setLaunchError] = useState('');
  // Declared early: boot rehydrate and server autosave both need it.
  const suppressServerAutosaveRef = useRef(false);
  // Block PUT until Mongo rehydrate finishes so a stale local buffer cannot
  // overwrite the cross-device source of truth on refresh.
  const bootRehydratePendingRef = useRef(!!openFileSession.currentFileId);
  // Invalidate in-flight boot when the user opens another deal or starts New.
  const sessionEpochRef = useRef(0);
  const dirtyServerPayloadRef = useRef(null);
  const dirtyServerFileIdRef = useRef(null);
  const lastPersistedFingerprintRef = useRef(null);
  const [bootHydrated, setBootHydrated] = useState(() => !openFileSession.currentFileId);
  const [notes, setNotes] = useState(() => autosave?.notes || '');
  const [cadastreData, setCadastreData] = useState(() => autosave?.cadastreData || null);
  const [marketData, setMarketData] = useState(() => {
    const m = autosave?.marketData || null;
    if (!m || m.status !== 'ok') return m;
    const isAi = m.data_type === 'ai_estimate' || m.source === 'AI Estimate';
    if (!isAi || m.query_key) return m;
    const addr = autosave?.inputs?.address || '';
    if (!addr || String(addr).trim().length < 5) return m;
    return { ...m, query_key: buildMarketQueryKey(addr) };
  });
  const [draftClampedNotice, setDraftClampedNotice] = useState(() => !!autosave?.draftWasClamped);

  // Server-backed file state — restored from autosave so browser refresh keeps the open file (Excel-like).
  const [currentFileId, setCurrentFileId] = useState(() => openFileSession.currentFileId);
  const [currentFileName, setCurrentFileName] = useState(() => openFileSession.currentFileName);
  const [projectVersion, setProjectVersion] = useState(0);

  // Load settings from server on mount + run migration
  useEffect(() => {
    migrateFromLocalStorage();
    fetchSettings().then(serverSettings => {
      if (serverSettings && !autosave?.assumptions) {
        setAssumptionsRaw(serverSettings);
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist settings to server when assumptions change
  const setAssumptions = useCallback((next) => {
    setAssumptionsRaw((prev) => {
      const resolved = typeof next === 'function' ? next(prev) : next;
      saveSettings(resolved);
      return resolved;
    });
  }, []);

  // Keep the active version's copy in sync with the working inputs/assumptions.
  // Exact id only — falling back to scenarios[0] would overwrite version 1
  // while a switch is in flight.
  useEffect(() => {
    setScenarios((prev) => {
      const active = findScenario(prev, activeScenarioId);
      if (!active) return prev;
      const next = syncActiveScenario(prev, activeScenarioId, inputs, assumptions);
      const updated = findScenario(next, activeScenarioId);
      if (
        !updated
        || (
          JSON.stringify(active.inputs) === JSON.stringify(updated.inputs)
          && JSON.stringify(active.assumptions) === JSON.stringify(updated.assumptions)
        )
      ) {
        return prev;
      }
      return next;
    });
  }, [inputs, assumptions, activeScenarioId]);

  // Repair a stale/orphaned active id so the header and persist target stay aligned.
  useEffect(() => {
    if (!scenarios.length) return;
    if (findScenario(scenarios, activeScenarioId)) return;
    const fallback = preferredScenario(scenarios);
    if (fallback?.id) setActiveScenarioId(fallback.id);
  }, [scenarios, activeScenarioId]);

  // Auto-derive filename from address — drafts only (never overwrite a saved file's name)
  useEffect(() => {
    if (!currentFileId && inputs.address) {
      setCurrentFileName(deriveNameFromAddress(inputs));
    }
  }, [inputs.address, currentFileId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Declared before autosave effect — dependency array must not read this before init (TDZ crash).
  const [fxSnapshot, setFxSnapshot] = useState(() => autosave?.fxSnapshot || autosave?.assumptions?.fxSnapshot || null);

  // Autosave working buffer + open-file identity (so refresh does not force DRAFT)
  useEffect(() => {
    localStorage.setItem(AUTOSAVE_KEY, JSON.stringify(withOpenFileSession({
      inputs: validateInputs(inputs),
      assumptions,
      notes,
      cadastreData,
      marketData,
      fxSnapshot,
      scenarios: syncActiveScenario(scenarios, activeScenarioId, inputs, assumptions),
      activeScenarioId,
      cashTracker,
      landAssessment,
      stageFinanceAssumptions,
    }, { currentFileId, currentFileName })));
  }, [inputs, assumptions, notes, cadastreData, marketData, fxSnapshot, scenarios, activeScenarioId, cashTracker, landAssessment, stageFinanceAssumptions, currentFileId, currentFileName]);

  // ==============================
  // Backend-only calculation state
  // ==============================
  const [outputs, setOutputs] = useState(EMPTY_OUTPUTS);
  const [decision, setDecision] = useState(EMPTY_DECISION);
  const [qualityGrade, setQualityGrade] = useState({});
  const [fx, setFx] = useState(identityChfFx);
  const [outputsChf, setOutputsChf] = useState({});
  const [syncing, setSyncing] = useState(true);
  const [calcError, setCalcError] = useState(null);
  const syncVersion = useRef(0);
  const lastOpCurrencyRef = useRef((assumptions.operatingCurrency || 'CHF').toUpperCase());

  // Single empty-draft write. New and a missing restored deal both call this.
  const resetToEmptyDraft = useCallback(() => {
    applyEmptyDraft(materializeEmptyDraft({
      inputs: freshInputs(),
      assumptions: freshAssumptions(),
      emptyOutputs: EMPTY_OUTPUTS,
      emptyDecision: EMPTY_DECISION,
    }), {
      suppressServerAutosaveRef,
      lastPersistedFingerprintRef,
      dirtyServerPayloadRef,
      dirtyServerFileIdRef,
      lastOpCurrencyRef,
      setScenarios,
      setActiveScenarioId,
      setCashTracker,
      setLandAssessment,
      setStageFinanceAssumptions,
      setWorkspace,
      setInputs,
      setAssumptionsRaw,
      setNotes,
      setCadastreData,
      setMarketData,
      setFxSnapshot,
      setCurrentFileId,
      setCurrentFileName,
      setDraftClampedNotice,
      setFx,
      setOutputs,
      setOutputsChf,
      setDecision,
      setQualityGrade,
      setProjectVersion,
    });
  }, [freshInputs, freshAssumptions, setInputs]);

  // If autosave restored a file id, confirm it still exists and rehydrate
  // EVERY data point from Mongo so another device/browser is the source of truth.
  useEffect(() => {
    const restoredId = openFileSession.currentFileId;
    if (!restoredId) {
      bootRehydratePendingRef.current = false;
      return undefined;
    }
    const bootEpoch = sessionEpochRef.current;
    const finishBoot = () => {
      if (!isSessionEpochCurrent(bootEpoch, sessionEpochRef.current)) return;
      bootRehydratePendingRef.current = false;
      setBootHydrated(true);
    };
    checkDealAccess(restoredId).then((result) => {
      if (!isSessionEpochCurrent(bootEpoch, sessionEpochRef.current)) return;
      if (result.status === 'missing') {
        // Same slate as New, including currency/FX. Do not flush: the id is gone.
        resetToEmptyDraft();
        finishBoot();
        return;
      }
      if (result.status !== 'ok' || !result.deal) {
        // Offline / transient — keep local buffer and resume autosave.
        finishBoot();
        return;
      }
      const resolved = resolveBootDealFromServer(result.deal, autosave);
      const state = resolved.state;
      if (!state) {
        finishBoot();
        return;
      }
      // Server fields win; local-only cash launch may still need upload.
      if (!resolved.shouldUploadCash) {
        suppressServerAutosaveRef.current = true;
      }
      setScenarios(state.scenarios);
      setActiveScenarioId(state.activeScenarioId);
      setCashTracker(state.cashTracker);
      setLandAssessment(hydrateLandAssessmentStore(state.landAssessment));
      setStageFinanceAssumptions(hydrateStageFinanceAssumptionsStore(state.stageFinanceAssumptions));
      setInputs(state.inputs);
      setAssumptionsRaw(state.assumptions);
      setNotes(state.notes);
      setCadastreData(state.cadastreData);
      setMarketData(state.marketData);
      setFxSnapshot(state.fxSnapshot);
      setCurrentFileId(state.id);
      setCurrentFileName(state.name);
      setOutputs(EMPTY_OUTPUTS);
      setOutputsChf({});
      setDecision(EMPTY_DECISION);
      setQualityGrade({});
      setProjectVersion((v) => v + 1);
      finishBoot();
    });
    return () => {
      // Unmount without Open/New still invalidates this boot (epoch fence).
      sessionEpochRef.current = bumpSessionEpoch(sessionEpochRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const version = ++syncVersion.current;
    const nextOp = (assumptions.operatingCurrency || 'CHF').toUpperCase();
    // Invalidate money display when operating currency changes — prevents mislabeling stale magnitudes.
    if (lastOpCurrencyRef.current !== nextOp) {
      lastOpCurrencyRef.current = nextOp;
      setOutputs(EMPTY_OUTPUTS);
      setOutputsChf({});
      setFx({ base: nextOp, quote: 'CHF', rate: 1, source: 'pending', mode: 'live', stale: true });
    }
    setSyncing(true);
    setCalcError(null);
    const timer = setTimeout(async () => {
      const result = await fetchCalculation(inputs, assumptions);
      if (syncVersion.current !== version) return;
      if (result) {
        setOutputs(result.outputs);
        setDecision(result.decision);
        setQualityGrade(result.qualityGrade || {});
        setFx(result.fx || { base: 'CHF', quote: 'CHF', rate: 1, source: 'identity', mode: 'live', stale: false });
        setOutputsChf(result.outputsChf || {});
        setCalcError(null);
      } else {
        setOutputsChf({});
        setCalcError('Calculation or FX failed — reporting amounts unavailable until sync succeeds.');
      }
      setSyncing(false);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputs, assumptions]);

  const handleRefreshFx = useCallback(() => {
    setAssumptionsRaw((prev) => {
      const next = { ...prev, fxMode: 'live' };
      delete next.fxSnapshot;
      return next;
    });
    setFxSnapshot(null);
  }, []);

  // File workflow state
  const [saveAsOpen, setSaveAsOpen] = useState(false);
  // 'first' = empty/new draft → cloud; 'copy' = Duplicate (explicit, separate process).
  const [saveAsMode, setSaveAsMode] = useState('first');
  const saveAsModeRef = useRef('first');
  const [financingOpen, setFinancingOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
    const [analysisTab, setAnalysisTab] = useState('model');
  const [inspectorOpen, setInspectorOpen] = useState(true);
  const [summaryExpanded, setSummaryExpanded] = useState(true);
  const [sidebarPinned, setSidebarPinned] = useState(() => (
    typeof window !== 'undefined' && window.matchMedia('(min-width: 1280px)').matches
  ));
  // Projects sidebar stays collapsed until the user opens it (header toggle).
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // After successful save/autosave, patch the sidebar list cache with server verdict.
  const [dealVerdictReconcile, setDealVerdictReconcile] = useState(null);

  // Sticky header is in-flow — do not also add margin-top (that leaves a dead gap).
  const mainHeightClass = 'h-[calc(100vh-76px)] md:h-[calc(100vh-2.75rem)]';

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1280px)');
    const sync = () => setSidebarPinned(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  // === Server autosave (saved files only) ===
  const [saveFlash, setSaveFlash] = useState(false);
  const [persistError, setPersistError] = useState('');
  const saveFlashTimerRef = useRef(null);
  const serverAutosaveTimerRef = useRef(null);
  const serialDealSaveRef = useRef(createSerialDealSaver());

  const flashSaved = useCallback(() => {
    setPersistError('');
    setSaveFlash(true);
    if (saveFlashTimerRef.current) clearTimeout(saveFlashTimerRef.current);
    saveFlashTimerRef.current = setTimeout(() => setSaveFlash(false), 1500);
  }, []);

  const markServerSynced = useCallback((payload) => {
    lastPersistedFingerprintRef.current = dealPersistFingerprint(payload);
    dirtyServerPayloadRef.current = null;
    dirtyServerFileIdRef.current = null;
  }, []);

  const buildPersist = useCallback((
    nextInputs = inputs,
    nextAssumptions = assumptions,
    nextNotes = notes,
    nextCadastre = cadastreData,
    nextMarket = marketData,
    nextFx = fxSnapshot,
    nextScenarios = scenarios,
    nextActive = activeScenarioId,
    nextCash = cashTracker,
    nextLand = landAssessment,
    nextStageFinance = stageFinanceAssumptions,
  ) => {
    const synced = syncActiveScenario(nextScenarios, nextActive, nextInputs, nextAssumptions);
    return buildDealPersistPayload(
      nextInputs,
      nextAssumptions,
      nextNotes,
      nextCadastre,
      nextMarket,
      nextFx,
      synced,
      nextActive,
      nextCash,
      nextLand,
      nextStageFinance,
    );
  }, [inputs, assumptions, notes, cadastreData, marketData, fxSnapshot, scenarios, activeScenarioId, cashTracker, landAssessment, stageFinanceAssumptions]);

  const persistDeal = useCallback(async (fileId, payload) => {
    const outcome = await serialDealSaveRef.current.enqueue(fileId, payload, updateDealDetailed);
    if (outcome?.coalesced) return;
    const mutation = outcome?.result;
    if (mutation?.data) {
      lastPersistedFingerprintRef.current = dealPersistFingerprint(outcome.payload);
      flashSaved();
      const patch = dealVerdictPatchFromServer(mutation.data, fileId);
      if (patch) setDealVerdictReconcile(patch);
    } else if (outcome?.payload) {
      dirtyServerPayloadRef.current = outcome.payload;
      dirtyServerFileIdRef.current = outcome.fileId;
      setPersistError(persistErrorMessage(mutation?.status));
    }
  }, [flashSaved]);

  const flushServerAutosave = useCallback(async () => {
    if (serverAutosaveTimerRef.current) {
      clearTimeout(serverAutosaveTimerRef.current);
      serverAutosaveTimerRef.current = null;
    }
    const fileId = dirtyServerFileIdRef.current;
    const payload = dirtyServerPayloadRef.current;
    if (!fileId || !payload) return;
    dirtyServerPayloadRef.current = null;
    dirtyServerFileIdRef.current = null;
    await persistDeal(fileId, payload);
  }, [persistDeal]);

  // Debounced PUT while a saved file is open; drafts never hit the server here.
  useEffect(() => {
    if (!currentFileId) {
      if (serverAutosaveTimerRef.current) {
        clearTimeout(serverAutosaveTimerRef.current);
        serverAutosaveTimerRef.current = null;
      }
      dirtyServerPayloadRef.current = null;
      dirtyServerFileIdRef.current = null;
      return undefined;
    }

    if (bootRehydratePendingRef.current) {
      return undefined;
    }

    if (suppressServerAutosaveRef.current) {
      suppressServerAutosaveRef.current = false;
      const payload = buildPersist();
      markServerSynced(payload);
      return undefined;
    }

    const payload = buildPersist();
    const fingerprint = dealPersistFingerprint(payload);
    if (!shouldServerAutosave(currentFileId, fingerprint, lastPersistedFingerprintRef.current)) {
      dirtyServerPayloadRef.current = null;
      dirtyServerFileIdRef.current = null;
      return undefined;
    }

    dirtyServerPayloadRef.current = payload;
    dirtyServerFileIdRef.current = currentFileId;
    if (serverAutosaveTimerRef.current) clearTimeout(serverAutosaveTimerRef.current);
    serverAutosaveTimerRef.current = setTimeout(() => {
      serverAutosaveTimerRef.current = null;
      const fileId = dirtyServerFileIdRef.current;
      const pending = dirtyServerPayloadRef.current;
      if (!fileId || !pending) return;
      dirtyServerPayloadRef.current = null;
      dirtyServerFileIdRef.current = null;
      persistDeal(fileId, pending);
    }, SERVER_AUTOSAVE_MS);

    return () => {
      if (serverAutosaveTimerRef.current) {
        clearTimeout(serverAutosaveTimerRef.current);
        serverAutosaveTimerRef.current = null;
      }
    };
  }, [inputs, assumptions, notes, cadastreData, marketData, fxSnapshot, scenarios, activeScenarioId, cashTracker, landAssessment, stageFinanceAssumptions, currentFileId, bootHydrated, markServerSynced, flashSaved, buildPersist, persistDeal]);

  // Flush pending server autosave when leaving the page (best-effort).
  useEffect(() => {
    const onBeforeUnload = () => {
      const fileId = dirtyServerFileIdRef.current;
      const payload = dirtyServerPayloadRef.current;
      if (!fileId || !payload) return;
      // Synchronous beacon not available for JSON PUT with cookies reliably;
      // clear timer and kick a last updateDeal (may complete if tab stays briefly).
      if (serverAutosaveTimerRef.current) {
        clearTimeout(serverAutosaveTimerRef.current);
        serverAutosaveTimerRef.current = null;
      }
      updateDeal(fileId, payload);
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  // === SAVE (update existing or prompt Save-As for drafts) ===
  const handleSave = useCallback(async () => {
    if (currentFileId) {
      if (serverAutosaveTimerRef.current) {
        clearTimeout(serverAutosaveTimerRef.current);
        serverAutosaveTimerRef.current = null;
      }
      const snap = fxToSnapshot(fx) || fxSnapshot;
      const frozenAssumptions = snap
        ? { ...assumptions, fxMode: 'frozen', fxSnapshot: snap, reportingCurrency: 'CHF' }
        : assumptions;
      if (snap) {
        setFxSnapshot(snap);
        setAssumptionsRaw(frozenAssumptions);
      }
      const payload = buildPersist(inputs, frozenAssumptions, notes, cadastreData, marketData, snap);
      dirtyServerPayloadRef.current = null;
      dirtyServerFileIdRef.current = null;
      const mutation = await updateDealDetailed(currentFileId, payload);
      if (mutation.data) {
        markServerSynced(payload);
        flashSaved();
        const patch = dealVerdictPatchFromServer(mutation.data, currentFileId);
        if (patch) setDealVerdictReconcile(patch);
      } else {
        setPersistError(persistErrorMessage(mutation.status));
      }
    } else {
      // True drafts only — never treat a demoted existing project as "new".
      saveAsModeRef.current = 'first';
      setSaveAsMode('first');
      setSaveAsOpen(true);
    }
  }, [currentFileId, inputs, assumptions, notes, cadastreData, marketData, fx, fxSnapshot, buildPersist, markServerSynced, flashSaved]);

  // Global Save shortcut — same action in every workspace (evaluation / land / cash).
  useEffect(() => {
    const onKeyDown = (e) => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 's') return;
      e.preventDefault();
      handleSave();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleSave]);

  const handleSaveAsNew = useCallback(() => {
    saveAsModeRef.current = 'copy';
    setSaveAsMode('copy');
    setSaveAsOpen(true);
  }, []);

  const handleSaveAsConfirm = useCallback(async (name) => {
    await flushServerAutosave();
    const snap = fxToSnapshot(fx) || fxSnapshot;
    const frozenAssumptions = snap
      ? { ...assumptions, fxMode: 'frozen', fxSnapshot: snap, reportingCurrency: 'CHF' }
      : assumptions;
    if (snap) {
      setFxSnapshot(snap);
      setAssumptionsRaw(frozenAssumptions);
    }
    // Duplicate copies keep analysis data but start without a launched cash ledger.
    const isCopy = saveAsModeRef.current === 'copy';
    const persistExtra = buildPersist(
      inputs,
      frozenAssumptions,
      notes,
      cadastreData,
      marketData,
      snap,
      undefined,
      undefined,
      isCopy ? null : cashTracker,
      landAssessment,
    );
    const dealMutation = await createDealDetailed(name, inputs, frozenAssumptions, notes, cadastreData, marketData, snap, {
      scenarios: persistExtra.scenarios,
      activeScenarioId: persistExtra.activeScenarioId,
      cashTracker: persistExtra.cashTracker,
      landAssessment: persistExtra.landAssessment,
      stageFinanceAssumptions: persistExtra.stageFinanceAssumptions,
    });
    const deal = dealMutation.data;
    if (!deal) {
      setPersistError(persistErrorMessage(dealMutation.status));
      return false;
    }
    if (isCopy) {
      setCashTracker(null);
    }
    setWorkspace('evaluation');
    suppressServerAutosaveRef.current = true;
    markServerSynced(persistExtra);
    setCurrentFileId(deal.id);
    setCurrentFileName(deal.name);
    flashSaved();
    return true;
  }, [inputs, assumptions, notes, cadastreData, marketData, fx, fxSnapshot, cashTracker, landAssessment, buildPersist, flushServerAutosave, markServerSynced, flashSaved]);

  // === OPEN — single coherent state replacement from the server deal ===
  const handleOpenFile = useCallback(async (deal) => {
    await flushServerAutosave();
    const state = materializeOpenedDeal(deal);
    if (!state) return;
    // Cancel any in-flight boot rehydrate so a late 404/ok cannot demote or overwrite.
    sessionEpochRef.current = bumpSessionEpoch(sessionEpochRef.current);
    bootRehydratePendingRef.current = false;
    setBootHydrated(true);
    suppressServerAutosaveRef.current = true;
    markServerSynced(state.persistPayload);
    setScenarios(state.scenarios);
    setActiveScenarioId(state.activeScenarioId);
    setCashTracker(state.cashTracker);
    setLandAssessment(hydrateLandAssessmentStore(state.landAssessment));
    setStageFinanceAssumptions(hydrateStageFinanceAssumptionsStore(state.stageFinanceAssumptions));
    setWorkspace('evaluation');
    setInputs(state.inputs);
    setAssumptionsRaw(state.assumptions);
    setNotes(state.notes);
    setCadastreData(state.cadastreData);
    setMarketData(state.marketData);
    setFxSnapshot(state.fxSnapshot);
    setCurrentFileId(state.id);
    setCurrentFileName(state.name);
    // Clear stale calculation state so targets don't leak between projects
    setOutputs(EMPTY_OUTPUTS);
    setOutputsChf({});
    setDecision(EMPTY_DECISION);
    setQualityGrade({});
    setProjectVersion(v => v + 1);
  }, [setInputs, flushServerAutosave, markServerSynced]);

  // Restore analysis when returning from document preview
  useEffect(() => {
    const openDealId = location.state?.openDealId;
    if (!openDealId) return;
    fetchDeal(openDealId).then((deal) => {
      if (deal) handleOpenFile(deal);
    });
  }, [location.state?.openDealId, handleOpenFile]);

  // === NEW — clean slate ===
  const handleNew = useCallback(async () => {
    await flushServerAutosave();
    sessionEpochRef.current = bumpSessionEpoch(sessionEpochRef.current);
    bootRehydratePendingRef.current = false;
    setBootHydrated(true);
    resetToEmptyDraft();
  }, [flushServerAutosave, resetToEmptyDraft]);

  const applyActivated = useCallback((result) => {
    if (!result.switched) return;
    const nextAssumptions = resolveVersionAssumptionsOnOpen(
      result.assumptions,
      result.assumptions?.fxSnapshot || fxSnapshot,
    );
    const nextFx = nextAssumptions.fxSnapshot ?? null;
    const addressChanged = String(result.inputs?.address || '').trim() !== String(inputs?.address || '').trim();
    const nextCadastre = addressChanged ? null : cadastreData;
    const nextMarket = addressChanged ? null : marketData;
    setScenarios(result.scenarios);
    setActiveScenarioId(result.activeScenarioId);
    setInputs(result.inputs);
    setAssumptionsRaw(nextAssumptions);
    setFxSnapshot(nextFx);
    if (addressChanged) {
      setCadastreData(null);
      setMarketData(null);
    }
    setOutputs(EMPTY_OUTPUTS);
    setOutputsChf({});
    setDecision(EMPTY_DECISION);
    setQualityGrade({});
    setProjectVersion((v) => v + 1);
    if (currentFileId) {
      const payload = buildPersist(
        result.inputs,
        nextAssumptions,
        notes,
        nextCadastre,
        nextMarket,
        nextFx,
        result.scenarios,
        result.activeScenarioId,
        cashTracker,
        landAssessment,
      );
      dirtyServerPayloadRef.current = payload;
      dirtyServerFileIdRef.current = currentFileId;
      persistDeal(currentFileId, payload);
    }
  }, [setInputs, fxSnapshot, inputs, cadastreData, marketData, currentFileId, buildPersist, notes, cashTracker, landAssessment, persistDeal]);

  const handleSelectVersion = useCallback((id) => {
    applyActivated(activateScenario(scenarios, activeScenarioId, id, inputs, assumptions));
  }, [scenarios, activeScenarioId, inputs, assumptions, applyActivated]);

  const handleCreateVersion = useCallback((name) => {
    const flushed = syncActiveScenario(scenarios, activeScenarioId, inputs, assumptions);
    const { scenarios: nextList, newScenario } = duplicateScenario(flushed, activeScenarioId, name);
    applyActivated({
      switched: true,
      scenarios: nextList,
      activeScenarioId: newScenario.id,
      inputs: deepClone(newScenario.inputs),
      assumptions: deepClone(newScenario.assumptions),
    });
  }, [scenarios, activeScenarioId, inputs, assumptions, applyActivated]);

  const handleDuplicateVersion = useCallback((sourceId) => {
    const flushed = syncActiveScenario(scenarios, activeScenarioId, inputs, assumptions);
    const source = findScenario(flushed, sourceId) || getActiveScenario(flushed, sourceId);
    const { scenarios: nextList, newScenario } = duplicateScenario(
      flushed,
      sourceId,
      uniqueCopyName(flushed, source?.name),
    );
    applyActivated({
      switched: true,
      scenarios: nextList,
      activeScenarioId: newScenario.id,
      inputs: deepClone(newScenario.inputs),
      assumptions: deepClone(newScenario.assumptions),
    });
  }, [scenarios, activeScenarioId, inputs, assumptions, applyActivated]);

  const handleRenameVersion = useCallback((id, name) => {
    setScenarios((prev) => renameScenario(prev, id, name));
  }, []);

  const handleDeleteVersion = useCallback((id) => {
    const flushed = syncActiveScenario(scenarios, activeScenarioId, inputs, assumptions);
    const result = deleteScenario(flushed, activeScenarioId, id);
    if (!result.deleted) return;
    if (result.activeScenarioId !== activeScenarioId) {
      const next = findScenario(result.scenarios, result.activeScenarioId)
        || getActiveScenario(result.scenarios, result.activeScenarioId);
      applyActivated({
        switched: true,
        scenarios: result.scenarios,
        activeScenarioId: result.activeScenarioId,
        inputs: deepClone(next?.inputs),
        assumptions: deepClone(next?.assumptions),
      });
    } else {
      setScenarios(result.scenarios);
    }
  }, [scenarios, activeScenarioId, inputs, assumptions, applyActivated]);

  const handleNewVersion = useCallback(() => {
    const label = window.prompt('New version name', 'New version');
    if (label == null) return;
    handleCreateVersion(label.trim() || 'New version');
  }, [handleCreateVersion]);

  const handleRenameProject = useCallback(async () => {
    if (!currentFileId) {
      setSaveAsOpen(true);
      return;
    }
    const next = window.prompt('Rename project', currentFileName || '');
    if (next == null) return;
    const name = next.trim();
    if (!name || name === currentFileName) return;
    const saved = await updateDeal(currentFileId, { name });
    if (saved) {
      setCurrentFileName(name);
      flashSaved();
    }
  }, [currentFileId, currentFileName, flashSaved]);

  const handleExportProject = useCallback(() => {
    const payload = {
      name: currentFileName || deriveNameFromAddress(inputs) || 'export',
      inputs,
      assumptions,
      notes,
      cadastreData,
      marketData,
      fxSnapshot: fxSnapshot || fxToSnapshot(fx),
      scenarios,
      activeScenarioId,
      exportedAt: new Date().toISOString(),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(payload.name || 'project').replace(/[^\w.-]+/g, '_')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [currentFileName, inputs, assumptions, notes, cadastreData, marketData, fxSnapshot, fx, scenarios, activeScenarioId]);

  const handleArchiveProject = useCallback(async () => {
    if (!currentFileId) return;
    if (!window.confirm('Archive this project?')) return;
    const ok = await archiveDeal(currentFileId);
    if (ok) await handleNew();
  }, [currentFileId, handleNew]);

  const handleDeleteProject = useCallback(async () => {
    if (!currentFileId) return;
    if (!window.confirm('Delete this project permanently?')) return;
    const ok = await deleteDeal(currentFileId);
    if (ok) await handleNew();
  }, [currentFileId, handleNew]);

  const handleApproveBaseline = useCallback(async (scenarioId) => {
    if (!currentFileId) {
      setLaunchError('Save this development first.');
      return;
    }
    const scenario = scenarios.find((s) => s.id === scenarioId) || getActiveScenario(scenarios, scenarioId);
    if (!scenario) {
      setLaunchError('Choose a version.');
      return;
    }
    setLaunchBusy(true);
    setLaunchError('');
    const result = await fetchCalculation(scenario.inputs || {}, scenario.assumptions || {});
    if (!result?.outputs) {
      setLaunchBusy(false);
      setLaunchError('Could not calculate this version. Check inputs and try again.');
      return;
    }
    const currency = (scenario.assumptions?.operatingCurrency || assumptions.operatingCurrency || 'CHF').toUpperCase();
    const baseline = buildApprovedBaseline({
      outputs: result.outputs,
      scenario,
      currency,
    });
    const next = createCashTracker({
      baseline,
      defaultEntity: currentFileName || 'Development',
    });
    // Persist to Mongo immediately — Cash Tracker must not live only in this browser.
    if (serverAutosaveTimerRef.current) {
      clearTimeout(serverAutosaveTimerRef.current);
      serverAutosaveTimerRef.current = null;
    }
    const payload = buildPersist(
      inputs,
      assumptions,
      notes,
      cadastreData,
      marketData,
      fxSnapshot,
      scenarios,
      activeScenarioId,
      next,
    );
    dirtyServerPayloadRef.current = null;
    dirtyServerFileIdRef.current = null;
    const mutation = await updateDealDetailed(currentFileId, payload);
    if (!mutation.data) {
      setLaunchBusy(false);
      setLaunchError(
        mutation.authExpired
          ? 'Session expired. Sign in again to save Cash Tracker.'
          : 'Could not save Cash Tracker to the server. Check your connection and try again.',
      );
      return;
    }
    markServerSynced(payload);
    setCashTracker(next);
    setWorkspace('cash');
    setLaunchOpen(false);
    setLaunchBusy(false);
    flashSaved();
  }, [
    currentFileId,
    currentFileName,
    scenarios,
    assumptions,
    inputs,
    notes,
    cadastreData,
    marketData,
    fxSnapshot,
    activeScenarioId,
    buildPersist,
    markServerSynced,
    flashSaved,
  ]);

  const launched = isLaunched(cashTracker);

  const activeLandAssessment = useMemo(
    () => getAssessmentForScenario(landAssessment, activeScenarioId),
    [landAssessment, activeScenarioId],
  );
  const combinedVerdict = useMemo(
    () => buildCombinedVerdict({
      qualityGrade,
      decision,
      assessment: activeLandAssessment,
    }),
    [qualityGrade, decision, activeLandAssessment],
  );


  const operatingCurrency = (assumptions.operatingCurrency || 'CHF').toUpperCase();
  const countryCode = (assumptions.countryCode || 'CH').toUpperCase();
  const displayMoney = {
    profitAfterTax: outputsChf.profitAfterTax != null
      ? outputsChf.profitAfterTax
      : (operatingCurrency === 'CHF' ? outputs.profitAfterTax : null),
    finalNetProfit: outputsChf.finalNetProfit != null
      ? outputsChf.finalNetProfit
      : (operatingCurrency === 'CHF' ? outputs.finalNetProfit : null),
    capitalInvested: outputsChf.capitalInvested != null
      ? outputsChf.capitalInvested
      : (operatingCurrency === 'CHF' ? outputs.capitalInvested : null),
    totalFinancingCost: outputsChf.totalFinancingCost != null
      ? outputsChf.totalFinancingCost
      : (operatingCurrency === 'CHF' ? outputs.totalFinancingCost : null),
  };

  return (
    <ErrorBoundary>
    <DealCurrencyProvider
      operatingCurrency={operatingCurrency}
      countryCode={countryCode}
      fx={fx}
      outputs={outputs}
      outputsChf={outputsChf}
      taxLabel={assumptions.taxLabel || 'IBGI'}
    >
    <div className="App min-h-screen bg-gray-100 [--app-header-offset:76px] md:[--app-header-offset:2.75rem]" data-testid="app-container">
      {calcError && (
        <div
          className="fixed left-0 right-0 z-[60] bg-red-50 border-b border-red-200 px-3 py-1 flex items-center justify-between gap-2"
          style={{ top: 'var(--app-header-offset, 76px)' }}
          data-testid="calc-error-banner"
        >
          <span className="text-[10px] text-red-800">{calcError}</span>
          <button type="button" className="text-[10px] font-bold text-red-700 hover:text-red-900" onClick={() => setCalcError(null)} aria-label="Dismiss calculate error">Dismiss</button>
        </div>
      )}
      {persistError && (
        <div
          className="fixed left-0 right-0 z-[60] bg-amber-50 border-b border-amber-200 px-3 py-1 flex items-center justify-between gap-2"
          style={{ top: `calc(var(--app-header-offset, 76px) + ${calcError ? 28 : 0}px)` }}
          data-testid="persist-error-banner"
        >
          <span className="text-[10px] text-amber-900">{persistError}</span>
          <div className="flex items-center gap-2 shrink-0">
            {persistError === SESSION_EXPIRED_SAVE_MSG && (
              <button
                type="button"
                className="text-[10px] font-bold text-amber-900 hover:text-amber-950 underline"
                onClick={() => { window.location.href = authLoginUrl(API_BASE_URL); }}
                aria-label="Sign in again"
                data-testid="persist-error-signin"
              >
                Sign in
              </button>
            )}
            <button
              type="button"
              className="text-[10px] font-bold text-amber-800 hover:text-amber-950"
              onClick={() => { setPersistError(''); flushServerAutosave(); }}
              aria-label="Retry save"
            >
              Retry
            </button>
          </div>
        </div>
      )}
      {draftClampedNotice && (
        <div
          className="fixed left-0 right-0 z-[60] bg-amber-50 border-b border-amber-200 px-3 py-1 flex items-center justify-between gap-2"
          style={{ top: `calc(var(--app-header-offset, 76px) + ${calcError || persistError ? 28 : 0}px)` }}
          data-testid="draft-clamped-banner"
        >
          <span className="text-[10px] text-amber-800">Draft had invalid/extreme values and was clamped to safe ranges.</span>
          <button type="button" className="text-[10px] font-bold text-amber-700 hover:text-amber-900" onClick={() => setDraftClampedNotice(false)} aria-label="Dismiss draft notice">Dismiss</button>
        </div>
      )}
      <KPIHeader
        roiOnEquity={outputs.roiOnEquity}
        verdict={combinedVerdict.verdict}
        reason={combinedVerdict.reason}
        landScore={combinedVerdict.landScore}
        onSwitchView={() => setWorkspace((w) => nextProjectView(w, { includeCash: launched }))}
        currentFileName={currentFileName}
        isDraft={!currentFileId}
        saveFlash={saveFlash}
        onToggleSidebar={() => setSidebarOpen((v) => !v)}
        sidebarOpen={sidebarOpen}
        inspectorOpen={inspectorOpen}
        onToggleInspector={() => setInspectorOpen((v) => !v)}
        summaryExpanded={summaryExpanded}
        onToggleSummary={() => setSummaryExpanded((v) => !v)}
        summarySlot={(
          <>
            <span>PAT <strong>{displayMoney.profitAfterTax != null ? Math.round(displayMoney.profitAfterTax).toLocaleString() : '—'}</strong></span>
            <span>Net <strong>{displayMoney.finalNetProfit != null ? Math.round(displayMoney.finalNetProfit).toLocaleString() : '—'}</strong></span>
            <span>Capital <strong>{displayMoney.capitalInvested != null ? Math.round(displayMoney.capitalInvested).toLocaleString() : '—'}</strong></span>
            <span>Funding <strong>{outputs.fundingCoverageRatio != null ? `${Math.round(outputs.fundingCoverageRatio * 100)}%` : '—'}</strong></span>
          </>
        )}
        versionSlot={(
          <ScenarioVersionControls
            size="compact"
            scenarios={scenarios}
            activeScenarioId={activeScenarioId}
            onSelect={handleSelectVersion}
            onCreate={handleCreateVersion}
            onRename={handleRenameVersion}
            onDuplicate={handleDuplicateVersion}
            onDelete={handleDeleteVersion}
          />
        )}
        onNewVersion={handleNewVersion}
        onDuplicate={handleSaveAsNew}
        onExport={handleExportProject}
        onArchive={currentFileId ? handleArchiveProject : undefined}
        onDelete={currentFileId ? handleDeleteProject : undefined}
        onOpenSettings={() => setFinancingOpen(true)}
        onSave={handleSave}
        onShare={() => setShareOpen(true)}
        launched={launched}
        workspace={workspace}
        onWorkspaceChange={setWorkspace}
        onLaunch={() => { setLaunchError(''); setLaunchOpen(true); }}
        lockSlot={<UserMenu user={user} />}
      />

      <AssumptionsModal
        hideTrigger
        open={financingOpen}
        onOpenChange={setFinancingOpen}
        assumptions={assumptions}
        onAssumptionsChange={setAssumptions}
        fx={fx}
        onRefreshFx={handleRefreshFx}
      />

      <ApproveBaselineDialog
        open={launchOpen}
        scenarios={scenarios}
        activeScenarioId={activeScenarioId}
        isDraft={!currentFileId}
        busy={launchBusy}
        error={launchError}
        onClose={() => { setLaunchOpen(false); setLaunchError(''); }}
        onConfirm={handleApproveBaseline}
      />

      <SaveAsDialog
        open={saveAsOpen}
        onOpenChange={setSaveAsOpen}
        inputs={inputs}
        mode={saveAsMode}
        onSave={handleSaveAsConfirm}
      />
      <ShareDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        name={currentFileName || deriveNameFromAddress(inputs) || 'Shared analysis'}
        dealId={currentFileId}
        inputs={inputs}
        assumptions={assumptions}
        notes={notes}
        cadastreData={cadastreData}
        marketData={marketData}
        fxSnapshot={fxSnapshot || fxToSnapshot(fx)}
        landAssessment={landAssessment}
        activeScenarioId={activeScenarioId}
      />


      <div
        className={`flex w-full min-w-0 ${mainHeightClass}`}
        data-testid="app-body"
      >
        {sidebarOpen && sidebarPinned ? (
          <ProjectSidebar
            open
            pinned
            onClose={() => setSidebarOpen(false)}
            onOpenProject={handleOpenFile}
            onNewProject={handleNew}
            currentFileId={currentFileId}
            activeLiveVerdict={currentFileId ? combinedVerdict : null}
            dealVerdictReconcile={dealVerdictReconcile}
          />
        ) : null}

        {workspace === 'cash' && launched ? (
          <div
            className="flex-1 min-w-0 lg:overflow-y-auto"
            data-testid="cash-workspace"
          >
            <CashTrackerView
              cashTracker={cashTracker}
              dealId={currentFileId}
              dealName={currentFileName}
              onChange={setCashTracker}
            />
          </div>
        ) : workspace === 'land' ? (
          <div
            className="flex-1 min-w-0 lg:overflow-y-auto"
            data-testid="land-workspace"
          >
            <LandAssessmentView
              landAssessment={landAssessment}
              scenarioId={activeScenarioId}
              scenarioName={scenarios.find((s) => s.id === activeScenarioId)?.name}
              dealId={currentFileId}
              dealName={currentFileName}
              address={inputs?.address}
              saveStatus={saveFlash ? 'saved' : (persistError ? 'error' : 'idle')}
              isDraft={!currentFileId}
              onSave={handleSave}
              onChange={setLandAssessment}
            />
          </div>
        ) : workspace === 'stageFinance' ? (
          <div
            className="flex-1 min-w-0 flex flex-col lg:flex-row"
            data-testid="stage-finance-workspace-shell"
          >
            <FinancialDriversPanel
              inputs={inputs}
              onInputChange={setInputs}
              assumptions={assumptions}
              onAssumptionsChange={setAssumptions}
              outputs={outputs}
              operatingCurrency={operatingCurrency}
              sourceLabels={sourceLabels || {}}
            />
            <div className="flex-1 min-w-0 lg:overflow-y-auto">
              <StageFinanceView
                outputs={outputs}
                assumptions={getStageFinanceAssumptionsForScenario(stageFinanceAssumptions, activeScenarioId)}
                onAssumptionsChange={(next) => {
                  setStageFinanceAssumptions((prev) =>
                    upsertStageFinanceAssumptionsForScenario(prev, activeScenarioId, next),
                  );
                }}
                onOpenPnL={() => setWorkspace('evaluation')}
              />
            </div>
          </div>
        ) : (
          <div
            className="flex-1 min-w-0 flex flex-col lg:flex-row gap-0 pb-14 md:pb-0"
            data-testid="main-layout"
          >
            <div className={analysisTab === 'model' ? 'contents' : 'hidden md:contents'}>
              <InputPanel key={activeScenarioId} inputs={inputs} onInputChange={setInputs} operatingCurrency={operatingCurrency} />
              <OutputPanel
                key={`out-${activeScenarioId}`}
                outputs={outputs}
                outputsChf={outputsChf}
                fx={fx}
                taxLabel={assumptions.taxLabel || 'IBGI'}
                operatingCurrency={operatingCurrency}
                countryCode={countryCode}
                warnings={decision.warnings}
                inputs={inputs}
                onInputChange={setInputs}
                assumptions={assumptions}
                onAssumptionsChange={setAssumptions}
                backendValidated={!syncing}
                notes={notes}
                onNotesChange={setNotes}
                dealId={currentFileId}
                onSave={handleSave}
              />
            </div>
            <DecisionCockpit
              key={projectVersion}
              visible={analysisTab === 'model' ? inspectorOpen : true}
              analysisTab={analysisTab === 'model' ? 'land' : analysisTab}
              className={analysisTab === 'model' ? 'hidden md:flex' : 'flex'}
              inputs={inputs}
              onInputChange={setInputs}
              outputs={outputs}
              outputsChf={outputsChf}
              fx={fx}
              decision={decision}
              qualityGrade={qualityGrade}
              combinedVerdict={combinedVerdict}
              assumptions={assumptions}
              notes={notes}
              cadastreData={cadastreData}
              onCadastreData={setCadastreData}
              marketData={marketData}
              onMarketData={setMarketData}
            />
          </div>
        )}
      </div>

      {sidebarOpen && !sidebarPinned ? (
        <ProjectSidebar
          open
          pinned={false}
          onClose={() => setSidebarOpen(false)}
          onOpenProject={handleOpenFile}
          onNewProject={handleNew}
          currentFileId={currentFileId}
          activeLiveVerdict={currentFileId ? combinedVerdict : null}
          dealVerdictReconcile={dealVerdictReconcile}
        />
      ) : null}

      <MobileAnalysisTabBar
        analysisTab={analysisTab}
        onAnalysisTabChange={setAnalysisTab}
        hidden={(workspace === 'cash' && launched) || workspace === 'land' || workspace === 'stageFinance'}
      />

    </div>
    </DealCurrencyProvider>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <>
      <BuildFreshnessBanner />
      <Routes>
      <Route path="/share/:token" element={<SharedAnalysisPage />} />
      <Route
        path="/*"
        element={(
          <GoogleAuthGate>
            {(user) => (
              <Routes>
                <Route path="/" element={<AnalysisPage user={user} />} />
                <Route path="/deals/:dealId/documents/:documentId" element={<DocumentPreviewPage />} />
              </Routes>
            )}
          </GoogleAuthGate>
        )}
      />
    </Routes>
    </>
  );
}

export default App;
